import { type MutableRefObject, useEffect, useRef } from "react";
import { type FieldPath, type UseFormReturn } from "react-hook-form";
import {
  getDefaultValues,
  type OrderItemConfigurationSchema,
} from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import {
  type OrderGetOrderDetailsOrderItemReadDto,
  type OrderItemDetailsReadDto,
} from "@/config/apiClient";

/**
 * Hook to preserve form values during backend updates
 *
 * **FRONTEND IS THE SOURCE OF TRUTH**
 *
 * This hook ensures that user changes in the form are NEVER overwritten
 * by backend data. Backend updates are only applied when the form is pristine
 * (no user modifications).
 *
 * Behavior:
 * - If the form is dirty (user has made changes): Keep ALL user changes, ignore backend updates
 * - If the form is pristine: Accept backend updates, but preserve focused fields and uploaded files
 */
export const usePreserveFormValues = (
  orderItem: OrderGetOrderDetailsOrderItemReadDto | OrderItemDetailsReadDto,
  methods: UseFormReturn<OrderItemConfigurationSchema>,
  focusedFieldRef: MutableRefObject<{
    path: FieldPath<OrderItemConfigurationSchema>;
    value: unknown;
  } | null>
) => {
  // Track the initial orderItem ID to detect when it actually changes
  const orderItemIdRef = useRef(orderItem.id);

  useEffect(() => {
    // Only reset if the orderItem ID has changed (navigating to a different item)
    // If the ID is the same, it means the backend just updated the same item
    const isNewOrderItem = orderItemIdRef.current !== orderItem.id;

    if (isNewOrderItem) {
      // This is a different order item, so we reset completely
      orderItemIdRef.current = orderItem.id;
      const defaultValues = getDefaultValues({ orderItem });
      methods.reset(defaultValues);
      return;
    }

    // Same order item - check if the form has user changes
    const formState = methods.formState;
    const isDirty = formState.isDirty;

    if (isDirty) {
      // FRONTEND IS SOURCE OF TRUTH
      // User has made changes, don't overwrite with backend data
      // The form keeps all user modifications
      return;
    }

    // Form is pristine (no user changes) - we can accept backend updates
    // but preserve focused fields and newly uploaded files
    const capturePreservableValues = () => {
      const focusedField = focusedFieldRef.current;
      const preMediaFiles = methods.getValues("preMediaFiles");

      return {
        focusedField: focusedField
          ? {
              path: focusedField.path,
              value: methods.getValues(focusedField.path),
            }
          : null,
        // Preserve files that are pending upload (have fileSignedId but no blobId yet)
        preMediaFiles: preMediaFiles?.some(
          (file) => file.fileSignedId && !file.blobId
        )
          ? preMediaFiles
          : null,
      };
    };

    const restorePreservedValues = (
      preserved: ReturnType<typeof capturePreservableValues>
    ) => {
      if (preserved.focusedField?.value !== undefined) {
        methods.setValue(
          preserved.focusedField.path,
          preserved.focusedField.value,
          { shouldDirty: true }
        );
      }

      if (preserved.preMediaFiles) {
        methods.setValue("preMediaFiles", preserved.preMediaFiles, {
          shouldDirty: true,
        });
      }
    };

    const preservedValues = capturePreservableValues();
    const defaultValues = getDefaultValues({ orderItem });
    methods.reset(defaultValues);
    restorePreservedValues(preservedValues);
  }, [orderItem, methods, focusedFieldRef]);
};
